<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link to="/admissions/cinema" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>{{ theatreCode }}</strong>
                    <span v-if="bucket.theatre_name" class="ml-2">{{ bucket.theatre_name }}<span v-if="bucket.city">, {{ bucket.city }}</span></span>
                    <span class="text-muted ml-2">— admissions per cinema year, all sessions vs. Brightfish preshow</span>
                </div>
                <form class="form-inline" @submit.prevent="calculate">
                    <label class="text-muted mr-2" for="last-years">Last</label>
                    <input id="last-years"
                           v-model.number="lastYears"
                           type="number"
                           min="1"
                           :max="visibleRows.length || 1"
                           class="form-control form-control-sm shx-th-60 text-right"
                           :disabled="loading || !visibleRows.length">
                    <span class="text-muted mx-2">years</span>
                    <button type="submit" class="btn btn-sm btn-outline-secondary" :disabled="loading || !visibleRows.length">
                        Calculate
                    </button>
                </form>
            </div>
        </div>

        <div v-if="result" class="box">
            <div class="box-content">
                <table class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th class="text-right shx-th-80"># Weeks</th>
                            <th class="text-right">All admissions</th>
                            <th class="text-right shx-th-120">All avg / week</th>
                            <th class="text-right">Preshow (BF)</th>
                            <th class="text-right shx-th-120">Preshow avg / week</th>
                            <th class="text-right shx-th-80">Preshow %</th>
                            <th class="text-right shx-th-80">Real %</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <strong>Last {{ result.years.length }} cinema year<span v-if="result.years.length !== 1">s</span></strong>
                                <span class="text-muted ml-2">{{ result.years[result.years.length - 1] }} – {{ result.years[0] }}</span>
                                <span v-if="result.capped" class="badge badge-warning ml-2"
                                      title="Fewer cinema years available than requested">only {{ result.years.length }} available</span>
                            </td>
                            <td class="text-right">{{ result.weeks }}</td>
                            <td class="text-right">{{ formatInt(result.admissions) }}</td>
                            <td class="text-right"><strong>{{ result.weeks ? formatInt(result.admissions / result.weeks) : '—' }}</strong></td>
                            <td class="text-right">{{ formatInt(result.admissions_preshow) }}</td>
                            <td class="text-right"><strong>{{ result.weeks ? formatInt(result.admissions_preshow / result.weeks) : '—' }}</strong></td>
                            <td class="text-right">{{ pct(result.admissions_preshow, result.admissions) }}</td>
                            <td class="text-right">
                                <span>{{ result.real_weighted != null ? result.real_weighted + '%' : '—' }}</span>
                                <span v-if="result.real_weighted != null && result.real_weighted <= 90"
                                      class="badge badge-warning ml-1">⚠</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!visibleRows.length" class="box">
            <div class="box-content text-center text-muted py-4">No admissions data found for {{ theatreCode }}.</div>
        </div>

        <div v-else class="box">
            <div class="box-content">
                <table class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th class="shx-th-100">Cinema year</th>
                            <th class="text-right shx-th-80"># Weeks</th>
                            <th class="text-right">All admissions</th>
                            <th class="text-right shx-th-120">All avg / week</th>
                            <th class="text-right">Preshow (BF)</th>
                            <th class="text-right shx-th-120">Preshow avg / week</th>
                            <th class="text-right shx-th-80">Preshow %</th>
                            <th class="text-right shx-th-80">Real %</th>
                            <th class="shx-th-100"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in visibleRows" :key="row.year">
                            <td>
                                <router-link :to="'/admissions/cinema/' + theatreCode + '/' + row.year">{{ row.year }}</router-link>
                            </td>
                            <td class="text-right">{{ row.weeks }}</td>
                            <td class="text-right">{{ formatInt(row.admissions) }}</td>
                            <td class="text-right">{{ row.avg_week !== null ? formatInt(row.avg_week) : '—' }}</td>
                            <td class="text-right">{{ formatInt(row.admissions_preshow) }}</td>
                            <td class="text-right">{{ row.avg_week_preshow !== null ? formatInt(row.avg_week_preshow) : '—' }}</td>
                            <td class="text-right">{{ row.pct_preshow !== null ? row.pct_preshow + '%' : '—' }}</td>
                            <td class="text-right">
                                <span>{{ row.real_weighted != null ? row.real_weighted + '%' : '—' }}</span>
                                <span v-if="row.real_weighted != null && row.real_weighted <= 90"
                                      class="badge badge-warning ml-1"
                                      :title="'Minimum real: ' + row.real_min + '%'">⚠</span>
                            </td>
                            <td>
                                <router-link :to="'/admissions/cinema/' + theatreCode + '/' + row.year"
                                             class="btn btn-sm btn-outline-secondary">
                                    Detail
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td class="text-right"><strong>{{ totalWeeks }}</strong></td>
                            <td class="text-right"><strong>{{ formatInt(totalAdmissions) }}</strong></td>
                            <td class="text-right"><strong>{{ totalWeeks ? formatInt(totalAdmissions / totalWeeks) : '—' }}</strong></td>
                            <td class="text-right"><strong>{{ formatInt(totalPreshow) }}</strong></td>
                            <td class="text-right"><strong>{{ totalWeeks ? formatInt(totalPreshow / totalWeeks) : '—' }}</strong></td>
                            <td class="text-right"><strong>{{ pct(totalPreshow, totalAdmissions) }}</strong></td>
                            <td colspan="2"></td>
                        </tr>
                    </tfoot>
                </table>
                <p class="text-muted small mt-2 mb-0">
                    A cinema year runs from week C01 to the last cinema week (Wednesday to Tuesday).
                    <span v-if="hiddenYears">{{ hiddenYears }} earlier year<span v-if="hiddenYears !== 1">s</span>
                    hidden (before {{ bucket.first_year }}, incomplete because of COVID); the API below returns them.</span>
                    <strong>All</strong> = every <code>admissions_week</code> row, <strong>Preshow</strong> = rows with
                    <code>saleshouse = BF</code>. <strong>Real %</strong> is the admissions-weighted average of
                    <code>real</code>: 97.5 = fully compiled from Cinedata, lower = partly estimated.
                    Same data as JSON: <code>GET /api/v1/admissions/cinema/{{ theatreCode }}</code>.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AdmissionsCinemaYears',
    props: ['theatreCode'],
    data() {
        return {
            loading: true,
            bucket: {},
            rows: [],
            lastYears: 3,
            result: null,
        };
    },
    computed: {
        /** The API returns every year; the page hides the incomplete COVID years before first_year. */
        visibleRows() {
            const first = parseInt(this.bucket.first_year, 10);
            return isNaN(first) ? this.rows : this.rows.filter(r => parseInt(r.year, 10) >= first);
        },
        hiddenYears() {
            return this.rows.length - this.visibleRows.length;
        },
        totalAdmissions() {
            return this.visibleRows.reduce((sum, r) => sum + (r.admissions || 0), 0);
        },
        totalWeeks() {
            return this.visibleRows.reduce((sum, r) => sum + (r.weeks || 0), 0);
        },
        totalPreshow() {
            return this.visibleRows.reduce((sum, r) => sum + (r.admissions_preshow || 0), 0);
        },
    },
    created() {
        this.loadYears();
    },
    watch: {
        theatreCode() {
            this.loadYears();
        },
    },
    methods: {
        loadYears() {
            this.loading = true;
            axios
                .get(`/admissions/cinema/${this.theatreCode}`)
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.rows = response.data.data.rows || [];
                    this.result = null;
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        /**
         * Average admissions per week over the N most recent cinema years.
         * Rows are sorted newest first; the current (partial) year counts with its own weeks.
         */
        calculate() {
            const n = Math.max(1, Math.floor(this.lastYears) || 1);
            const years = this.visibleRows.slice(0, n);

            if (!years.length) {
                this.result = null;
                return;
            }

            const admissions = years.reduce((sum, r) => sum + (r.admissions || 0), 0);
            const preshow = years.reduce((sum, r) => sum + (r.admissions_preshow || 0), 0);
            const weeks = years.reduce((sum, r) => sum + (r.weeks || 0), 0);
            const realNumerator = years.reduce((sum, r) => sum + (r.admissions || 0) * (r.real_weighted || 0), 0);

            this.lastYears = years.length;
            this.result = {
                years: years.map(r => r.year),
                capped: years.length < n,
                admissions,
                admissions_preshow: preshow,
                weeks,
                real_weighted: admissions ? Math.round(realNumerator / admissions * 10) / 10 : null,
            };
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
